import React from 'react';
import { Link } from 'react-router-dom';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import {
  AgroOverviewLocation,
  RiskLevel,
} from 'types/Meteorology/Agro/Overview/Dashboard';
import {
  RISK_LABEL,
  removerSufixoUf,
} from 'helper/Meteorology/Agro/Overview/locationsTable';

import './index.scss';

const DASH = '—';

// Mesmas cores dos badges de risco do desktop (SummaryCards/index.scss).
const RISK_CLASS: Record<RiskLevel, string> = {
  baixo: 'risk-low',
  moderado: 'risk-mid',
  alto: 'risk-high',
  muito_alto: 'risk-very-high',
};

function mm(value: number | null): string {
  return value === null || value === undefined ? DASH : `${value} mm`;
}

function dias(value: number | null): string {
  if (value === null || value === undefined) return DASH;
  return `${value} ${value === 1 ? 'dia' : 'dias'}`;
}

function displayName(location: AgroOverviewLocation): string {
  return location.estado ? removerSufixoUf(location.nome, location.estado) : location.nome;
}

// A nota reaproveita apenas dado real (risco7d) e sempre remete ao Balanço
// Hídrico Sequencial — sem afirmar números que a tela não tem (ex.: série diária).
function noteFor(risco: RiskLevel | null): string {
  const cta = 'Acompanhe a evolução na aba Balanço Hídrico — Sequencial.';
  if (risco === 'alto' || risco === 'muito_alto') {
    return `Risco de estresse hídrico elevado nos últimos 7 dias. ${cta}`;
  }
  if (risco === 'moderado') {
    return `Condições hídricas em atenção nos últimos 7 dias. ${cta}`;
  }
  if (risco === 'baixo') {
    return `Condições hídricas dentro do esperado nos últimos 7 dias. ${cta}`;
  }
  return cta;
}

function Tile({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="agro-overview-mobile-detail-tile">
      <span className="agro-overview-mobile-detail-tile-label">{label}</span>
      <span
        className={`agro-overview-mobile-detail-tile-value${valueClassName ? ` ${valueClassName}` : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

export default function LocationDetailSheet({
  location,
  dismiss,
}: {
  location: AgroOverviewLocation | null;
  dismiss: () => void;
}) {
  const risco = location?.risco7d ?? null;
  const saldo = location?.perdaAgua7dMm ?? null;

  // DECISÃO-10: perdaAgua7dMm é o saldo COM sinal. Déficit (negativo) e
  // excedente (positivo) são as duas metades do mesmo balanço, separadas
  // apenas para exibição — espelha os dois cards do protótipo.
  const deficit = saldo === null ? DASH : mm(saldo < 0 ? saldo : 0);
  const excedente = saldo === null ? DASH : mm(saldo > 0 ? saldo : 0);

  const name = location ? displayName(location) : '';
  const place = location?.estado ? `${location.estado} · Brasil` : 'Brasil';

  const header = location ? (
    <div className="agro-overview-mobile-detail-head">
      <div className="agro-overview-mobile-detail-head-text">
        <span className="agro-overview-mobile-detail-eyebrow">Localidade monitorada</span>
        <span className="agro-overview-mobile-detail-name">{name}</span>
        <span className="agro-overview-mobile-detail-place">{place}</span>
      </div>
      <span
        className={`agro-overview-mobile-detail-risk-badge${risco ? ` ${RISK_CLASS[risco]}` : ''}`}
      >
        {risco ? RISK_LABEL[risco] : DASH}
      </span>
    </div>
  ) : '';

  return (
    <BottomSheet
      open={location !== null}
      dismiss={dismiss}
      title={header}
      className="agro-overview-mobile-detail-sheet"
    >
      {location && (
        <div className="agro-overview-mobile-detail">
          <div className="agro-overview-mobile-detail-tiles">
            <Tile label="Dias sem chuva" value={dias(location.diasSemChuva)} />
            <Tile
              label="Armazenamento (ARM)"
              value={location.armPct === null ? DASH : `${location.armPct}%`}
            />
            <Tile label="Chuva prevista (7d)" value={mm(location.chuva7dMm)} />
            <Tile
              label="Déficit hídrico"
              value={deficit}
              valueClassName={saldo !== null && saldo < 0 ? 'negative' : undefined}
            />
            <Tile
              label="Excedente hídrico"
              value={excedente}
              valueClassName={saldo !== null && saldo > 0 ? 'positive' : undefined}
            />
            <Tile
              label="Risco de estresse"
              value={risco ? RISK_LABEL[risco] : DASH}
              valueClassName={risco ? RISK_CLASS[risco] : undefined}
            />
          </div>

          <p className="agro-overview-mobile-detail-note">{noteFor(risco)}</p>

          <div className="agro-overview-mobile-detail-actions">
            <button
              type="button"
              className="agro-overview-mobile-detail-close"
              onClick={dismiss}
            >
              Fechar
            </button>
            <Link
              to="/agro/water-balance-sequential/"
              className="agro-overview-mobile-detail-balance"
              onClick={dismiss}
            >
              Ver balanço hídrico
            </Link>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}
